import { DialogPanel } from '@headlessui/react'
import { useTranslation } from '@mntn-dev/i18n'
import {
  Badge,
  Heading,
  IconButton,
  LinkButton,
  Stack,
  Surface,
  type TestIds,
} from '@mntn-dev/ui-components'
import { motion } from 'motion/react'
import type { PropsWithChildren } from 'react'
import { modalSlideInVariants } from '../../ui-components/src/components/modal/utils.ts'
import { NotificationSurfaceSection } from './notification-surface-section.tsx'

type NotificationSurfaceProps = PropsWithChildren<
  {
    onClose: () => void
    onMarkAllAsRead: () => void
    unreadNotificationCount: number | undefined
  } & TestIds
>

const NotificationSurfaceComponent = ({
  onClose,
  onMarkAllAsRead,
  unreadNotificationCount = 0,
  children,
}: NotificationSurfaceProps) => {
  const { t } = useTranslation('notification')

  return (
    <DialogPanel
      as={motion.div}
      variants={modalSlideInVariants}
      initial="initial"
      animate="open"
      exit="initial"
      className="absolute left-24 top-1/2 -translate-y-1/2"
    >
      <Surface
        border
        divide={false}
        className="bg-container backdrop-blur-xl h-screen-minus-32 w-128 gap-0 p-0"
        dataTestId="notifications-modal"
        dataTrackingId="notifications-modal"
      >
        <Surface.Section className="bg-container-tertiary">
          <Stack gap="2" padding="6" justifyContent="between">
            <Stack gap="2">
              <Heading fontSize="xl">{t('title')}</Heading>
              {unreadNotificationCount > 0 && (
                <Badge
                  dataTestId="notifications-modal-unread-count-badge"
                  dataTrackingId="notifications-modal-unread-count-badge"
                  size="lg"
                  value={unreadNotificationCount}
                />
              )}
            </Stack>
            <Stack columnGap="10">
              {unreadNotificationCount > 0 && (
                <LinkButton
                  onClick={onMarkAllAsRead}
                  dataTestId="notifications-modal-mark-all-read-button"
                  dataTrackingId="notifications-modal-mark-all-read-button"
                  className="self-center"
                >
                  {t('actions.mark-all-read')}
                </LinkButton>
              )}
              <IconButton
                name="XMarkIcon"
                size="md"
                color="info"
                onClick={onClose}
                centered
              />
            </Stack>
          </Stack>
        </Surface.Section>
        <Surface.Section
          height="full"
          className="divide-y divide-subtle overflow-y-auto scroll-thin"
        >
          {children}
        </Surface.Section>
      </Surface>
    </DialogPanel>
  )
}

const NotificationSurfaceNamespace = Object.assign(
  NotificationSurfaceComponent,
  {
    Section: NotificationSurfaceSection,
  }
)

export {
  NotificationSurfaceNamespace as NotificationSurface,
  type NotificationSurfaceProps,
}
